import { useTranslation } from 'react-i18next';
import type { PropertySummary, TravelTime } from '../types/api';
import { SITE_DISPLAY_NAMES } from '../types/api';
import { Card } from './index';
import { MapPin, Star } from 'lucide-react';
import { trackEvent } from '../services/analytics';
import { formatFurnished, extractPostcode } from '../utils/format';

interface PropertyCardProps {
  property: PropertySummary;
  onClick: () => void;
  compareSelected?: boolean;
  onToggleCompare?: (id: string) => void;
  primaryLocationName?: string;
  active?: boolean;
}

const statusColors: Record<string, string> = {
  new: 'var(--info)',
  interested: 'var(--success)',
  viewing: 'var(--warning)',
  applied: '#7c3aed',
  rejected: 'var(--error)',
  excluded: 'var(--gray-400)',
  rented: 'var(--success)',
};

function getPrimaryCommute(travelTimes: TravelTime[], primaryLocationName?: string): TravelTime | undefined {
  if (!primaryLocationName || travelTimes.length === 0) return undefined;
  return travelTimes.find(tt => tt.locationName === primaryLocationName) ?? travelTimes[0];
}

export function PropertyCard({ property, onClick, compareSelected, onToggleCompare, primaryLocationName, active }: PropertyCardProps) {
  const { t } = useTranslation();
  const mainImage = property.images[0];
  const isMuted = property.status === 'excluded' || property.status === 'rejected';
  const postcode = extractPostcode(property.address);
  const primaryCommute = getPrimaryCommute(property.travelTimes, primaryLocationName);

  const handleClick = () => {
    trackEvent('select_property', { property_id: property.id, address: property.address, status: property.status });
    onClick();
  };

  const handleCompareClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onToggleCompare?.(property.id);
  };

  return (
    <Card className={`property-card${property.locked ? ' property-card-locked' : ''}${isMuted ? ' card-muted' : ''}${active ? ' property-card-active' : ''}`} onClick={handleClick}>
      {/* Map hero - always shown */}
      <div className="property-card-map">
        <iframe
          title={t('app.propertyCard.mapTitle', { address: property.address })}
          src={`https://maps.google.com/maps?q=${encodeURIComponent(property.address)}&t=&z=15&ie=UTF8&iwloc=&output=embed`}
          loading="lazy"
          referrerPolicy="no-referrer-when-downgrade"
        />
        {/* Status pill */}
        <span className="card-status-pill" style={{ background: statusColors[property.status] || 'var(--gray-400)' }}>
          {t(`status.${property.status}`)}
        </span>
        {property.isDemo && (
          <span className="card-demo-badge">{t('app.propertyCard.sampleBadge')}</span>
        )}
        {/* Open map link */}
        <a
          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(property.address)}`}
          target="_blank"
          rel="noopener noreferrer"
          className="property-card-map-link"
          onClick={(e) => e.stopPropagation()}
        >
          <MapPin size={12} aria-hidden="true" />
          {t('app.propertyCard.openMap')}
        </a>
        {/* Small image thumbnail if images exist */}
        {mainImage && (
          <div className="card-image-thumb">
            <img src={mainImage} alt="" />
          </div>
        )}
      </div>

      <div className="property-card-content">
        {/* Scan line */}
        <div className="card-scan-line">
          £{property.rent.toLocaleString()}{property.listingType === 'sale' ? '' : ` ${t('app.propertyCard.pcm')}`}
          {' · '}{property.bedrooms} {t('app.propertyCard.bed')}
          {' · '}{property.bathrooms} {t('app.propertyCard.bath')}
          {postcode && <>{' · '}{postcode}</>}
          {' · '}{formatFurnished(property.furnished)}
        </div>

        {/* Address */}
        <p className="property-address">{property.address}</p>

        {/* Primary commute strip */}
        {primaryCommute && (
          <div className="card-commute-strip">
            {primaryCommute.locationName}
            {primaryCommute.transitMinutes > 0 && <span>{' '}{t('app.propertyCard.transitMin', { mins: primaryCommute.transitMinutes })}</span>}
            {primaryCommute.walkingMinutes > 0 && <span>{' · '}{t('app.propertyCard.walkMin', { mins: primaryCommute.walkingMinutes })}</span>}
          </div>
        )}

        {/* Bottom bar */}
        <div className="card-bottom-bar">
          <div className="card-bottom-left">
            {property.userRating !== undefined && property.userRating > 0 && (
              <span className="property-rating" aria-label={t('app.ratingInput.starLabel', { count: property.userRating })}>
                {Array.from({ length: property.userRating }, (_, i) => (
                  <Star key={i} size={14} fill="currentColor" aria-hidden="true" />
                ))}
              </span>
            )}
            <span className="property-site">{SITE_DISPLAY_NAMES[property.site] || property.site}</span>
          </div>
          <div className="card-bottom-right">
            {onToggleCompare && (
              <label className="card-compare-check" onClick={handleCompareClick}>
                <input
                  type="checkbox"
                  checked={compareSelected ?? false}
                  onChange={() => {}}
                  tabIndex={-1}
                />
                <span>{t('app.propertyCard.compare')}</span>
              </label>
            )}
            <button className="card-view-btn" onClick={(e) => { e.stopPropagation(); handleClick(); }}>
              {t('app.propertyCard.view')}
            </button>
          </div>
        </div>
      </div>
    </Card>
  );
}
